import { ChangeEvent, useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Box, Button, Grid, InputAdornment } from '@mui/material';
import { Chip, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDebouncedCallback } from 'use-debounce';

import CustomBackdrop from '../../components/CustomBackdrop/CustomBackdrop';
import KeywordSelectionPopover from '../../components/KeywordSelectionPopover';
import PageHeader from '../../components/PageHeader';
import StyledSearchField from '../../components/StyledSearchField';
import { FLAG_URL } from '../../contants';
import {
  SearchCompanyDocument,
  useMarkCompanyAsSoftwareMutation,
  useSearchCompanyLazyQuery,
  useStartCrawlMutation,
} from '../../gql/generated/graphql';
import { useCompanyNavigation } from '../../hooks/useCompanyNavigation';
import usePagination from '../../hooks/usePagination';
import { useSnackbar } from '../../providers/SnackbarProvider';
import { formatUrl } from '../../utils/formatUrl';
import CustomPaper from '../DFMPage/components/CustomPaper/CustomPaper';

import styles from './styles';

const SearchPage = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { handleValidate, handleNavigateToAssesment } = useCompanyNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [crawlCompany, setCrawlCompany] = useState('');
  const crawlInputRef = useRef<HTMLInputElement>(null);

  const [isProcessingSearch, setIsProcessingSearch] = useState(false);
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null);

  const cleanUrl = (url: string): string => {
    if (!url) return url;

    let cleanedUrl = url.trim();

    cleanedUrl = cleanedUrl.replace(/^https?:\/\//, '');
    cleanedUrl = cleanedUrl.replace(/^http?:\/\//, '');

    cleanedUrl = cleanedUrl.replace(/^www\./, '');

    const domainMatch = cleanedUrl.match(/^([^\/]+)/);
    if (domainMatch) {
      cleanedUrl = domainMatch[1];
    }

    return cleanedUrl;
  };

  const [getCompanies, { data: companiesData, loading: getCompaniesLoading, fetchMore }] = useSearchCompanyLazyQuery({
    onCompleted: data => {
      const nodes = data?.cortexFirms?.edges?.map(node => node?.node) || [];

      if (nodes.length === 0 && searchTerm && !isProcessingSearch) {
        handleEmptySearchResults();
      }
    },
  });

  const [startCrawl, { loading: startCrawlLoading }] = useStartCrawlMutation();
  const [markCompanyAsSoftware, { loading: markCompanyAsSoftwareLoading }] = useMarkCompanyAsSoftwareMutation();

  const { paginationModel, isLoading, handlePaginationChange } = usePagination({
    fetchMore,
    pageInfo: companiesData?.cortexFirms?.pageInfo,
  });

  const handleEmptySearchResults = () => {
    if (isProcessingSearch) return;

    setIsProcessingSearch(true);
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    const timer = setTimeout(() => {
      if (searchTerm) {
        const cleanedUrl = cleanUrl(searchTerm);
        const { isValid } = formatUrl(`https://${cleanedUrl}`);
        if (isValid) {
          setCrawlCompany(cleanedUrl);
          setTimeout(() => {
            if (crawlInputRef.current) {
              crawlInputRef.current.focus();
            }
          }, 100);
        }
      }

      setIsProcessingSearch(false);
      setSearchTimer(null);
    }, 2000);

    setSearchTimer(timer);
  };

  useEffect(() => {
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  }, [searchTimer]);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    getCompanies({ variables: { searchTerm: value } });
  }, 500);

  const companiesNodes = companiesData?.cortexFirms?.edges?.map(node => node?.node);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (searchTimer) {
      clearTimeout(searchTimer);
      setSearchTimer(null);
    }
    setIsProcessingSearch(false);

    if (value) {
      const cleanedForSearch = cleanUrl(value);
      debouncedSearch(cleanedForSearch);
    } else {
      getCompanies({ variables: { searchTerm: '' } });
    }
  };

  const handleReset = () => {
    setSearchTerm('');

    if (searchTimer) {
      clearTimeout(searchTimer);
      setSearchTimer(null);
    }
    setIsProcessingSearch(false);

    const event = { target: { value: '' } } as ChangeEvent<HTMLInputElement>;
    handleInputChange(event);
  };

  const handleCrawlInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleForceCrawlByCompanyName();
    }
  };

  const handleForceCrawlByCompanyName = async () => {
    if (!crawlCompany) return;

    const { isValid, url } = formatUrl(crawlCompany);

    if (!isValid) {
      showSnackbar(t('searchPage.invalidUrl'), 'error', false);
      return;
    }

    const result = await startCrawl({
      variables: {
        domain: url,
        forceDeepAnalysis: true,
        forceSw: true,
      },
    });

    if (result?.data?.startCrawler?.success) {
      setCrawlCompany('');
    }
  };

  const searchColumnCategories = [
    {
      field: 'shortName',
      headerName: t('searchPage.shortNameColumnField'),
      width: 200,
    },
    {
      field: 'uniqueDomain',
      headerName: t('searchPage.uniqueDomainColumnField'),
      width: 200,
    },
    {
      field: 'country',
      headerName: t('searchPage.countryColumnField'),
      width: 100,
      renderCell: params => {
        return params?.formattedValue ? (
          <img src={FLAG_URL(params?.formattedValue)} width={32} height={32} alt="Company country flag" />
        ) : (
          ''
        );
      },
    },
    {
      field: 'validated',
      headerName: t('searchPage.validatedColumnField'),
      renderCell: params => {
        return (
          <Chip
            sx={params?.formattedValue ? styles.validatedChip : styles.notValidatedChip}
            label={params?.formattedValue ? t('searchPage.validatedChipLabel') : t('searchPage.notValidatedChipLabel')}
            size="small"
          />
        );
      },
      width: 150,
    },
    {
      field: 'isSoftwareCompany',
      headerName: t('searchPage.isSoftwareFirmColumnField'),
      renderCell: params => {
        return params?.row?.isSoftwareCompany ? <DoneIcon /> : <CloseIcon />;
      },
      width: 150,
    },
    {
      field: 'doNotCrawl',
      headerName: t('searchPage.doNotCrawlColumnField'),
      renderCell: params => {
        const doNotCrawl = params?.formattedValue?.doNotCrawl;
        return doNotCrawl ? <CloseIcon /> : null;
      },
      width: 100,
    },
    {
      field: 'actions',
      headerName: '',
      editable: false,
      sortable: false,
      width: 450,
      renderCell: params => {
        const isValidated = params?.row?.validated;
        const doNotCrawl = params?.formattedValue?.doNotCrawl;
        return (
          <>
            <IconButton disabled={isValidated} size="small" onClick={() => handleValidate(params.row?.dbId)}>
              <Chip
                sx={isValidated ? styles.validatedChipButton : styles.notValidatedChipButton}
                label={t('searchPage.validateButton')}
                size="small"
              />
            </IconButton>
            <IconButton
              disabled={!isValidated}
              size="small"
              onClick={() => handleNavigateToAssesment(params.row?.dbId)}
            >
              <Chip
                sx={isValidated ? styles.notValidatedChipButton : styles.validatedChipButton}
                label={t('searchPage.evaluateButton')}
                size="small"
              />
            </IconButton>
            <KeywordSelectionPopover
              companyId={params.row.dbId}
              validationType="software"
              onConfirm={() => handleMarkAsSoftwareCompany(params.row.dbId)}
            >
              <IconButton size="small">
                <Chip sx={styles.notValidatedChipButton} label={t('searchPage.revalidateButton')} size="small" />
              </IconButton>
            </KeywordSelectionPopover>
            <IconButton disabled={doNotCrawl} size="small" onClick={() => handleForceCrawl(params.row)}>
              <Chip
                sx={!doNotCrawl ? styles.notValidatedChipButton : styles.validatedChipButton}
                label={t('searchPage.forceCrawlButton')}
                size="small"
              />
            </IconButton>
          </>
        );
      },
    },
  ];

  const handleForceCrawl = async row => {
    await startCrawl({
      variables: {
        companyName: row?.shortName,
        domain: row?.uniqueDomain,
        forceSw: true,
      },
    });
  };

  const handleMarkAsSoftwareCompany = async (id: number) => {
    await markCompanyAsSoftware({
      variables: { companyId: id },
      refetchQueries: [
        {
          query: SearchCompanyDocument,
          variables: { searchTerm },
        },
      ],
    });
  };

  return (
    <Box display="flex" gap={2} flexDirection="column">
      <PageHeader
        title="Company Search"
        subtitle="Search and manage companies in the database"
        variant="search"
        searchComponent={
          <StyledSearchField
            placeholder={t('searchPage.searchbarPlaceholder')}
            variant="outlined"
            fullWidth
            onChange={handleInputChange}
            value={searchTerm}
            size="small"
            InputProps={{
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleReset} edge="end" size="small">
                    <ClearIcon sx={{ fontSize: '1.125rem' }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        }
      />
      <Box px={3}>
        <CustomPaper>
          <DataGrid
            paginationMode="server"
            loading={getCompaniesLoading || isLoading}
            getRowId={row => row?.dbId}
            disableRowSelectionOnClick
            rows={companiesNodes || []}
            columns={searchColumnCategories}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            rowHeight={35}
            initialState={{
              pagination: {
                rowCount: -1,
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10]}
          />
        </CustomPaper>
        <Grid container spacing={2}>
          <Grid item xs={10}>
            <StyledSearchField
              fullWidth
              size="small"
              placeholder={t('searchPage.crawlTextFieldPlaceholder')}
              value={crawlCompany}
              onChange={e => setCrawlCompany(e.target.value)}
              inputRef={crawlInputRef}
              onKeyDown={handleCrawlInputKeyDown}
              InputProps={{
                startAdornment: null, // Override default search icon for this field
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <Button fullWidth variant="contained" color="primary" onClick={handleForceCrawlByCompanyName}>
              {t('searchPage.forceCrawlButton')}
            </Button>
          </Grid>
        </Grid>
        <CustomBackdrop open={startCrawlLoading || markCompanyAsSoftwareLoading} />
      </Box>
    </Box>
  );
};

export default SearchPage;
